import type { ZodType } from 'zod';
import { sendAppSignalError } from '~/lib/appsignal.client';
import type { ErrorParser } from '~/lib/errors';
import { projectStore } from '~/lib/stores/project';
import { invariant } from '~/utils/invariant';
import { logger } from '~/utils/logger';

const textDecoder = new TextDecoder();

interface FetchStreamOptions<S extends ZodType> extends RequestInit {
  /**
   * Callback to receive the streamed content.
   *
   * @param partialContent Partial text received
   */
  onTextStream?: (partialContent: string) => void;

  /**
   * Type of the expected error.
   */
  errorParser: ErrorParser<S>;

  /**
   * Set this to true to ignore the error returned.
   */
  silenceError?: true;
}

// if silenceError is not set then the result is always a string otherwise the value can be undefined
type TextResult<O extends FetchStreamOptions<any>> = O['silenceError'] extends true ? string | undefined : string;

/**
 * A utility function to fetch streamed text.
 *
 * This function properly handles errors to avoid having them
 * being incorrectly treated as part of the expected streamed
 * response.
 */
export async function fetchStreamedText<E extends ZodType, O extends FetchStreamOptions<E>>(
  url: string | URL,
  options: O,
): Promise<TextResult<O>> {
  const { errorParser: errorClass, onTextStream, silenceError } = options;

  try {
    const response = await clientFetch(url, options);

    if (!response.ok || response.status !== 200) {
      throw await errorClass.parse(errorClass, response);
    }

    const reader = response.body?.getReader();

    invariant(reader, 'Expected reader');

    let text = '';

    while (true) {
      const { value, done } = await reader.read();

      if (done) {
        return text;
      }

      text += textDecoder.decode(value);

      onTextStream?.(text);
    }
  } catch (error) {
    logger.error(`Error while receiving streamed content\n\n${error}`);

    sendAppSignalError(error);

    if (!silenceError) {
      throw error;
    }

    return undefined as TextResult<O>;
  }
}

export function clientFetch(input: string | URL | Request, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);

  if (isSameOrigin(input, window.origin)) {
    headers.set('X-Bolt-Client-Revision', __COMMIT_HASH__);

    const projectId = projectStore.getProjectId();

    if (projectId) {
      headers.set('X-Bolt-Project-Id', projectId);
    }
  }

  // eslint-disable-next-line bolt/no-direct-fetch
  return fetch(input, {
    ...init,
    headers,
  });
}

function isSameOrigin(a: string | URL | Request, b: string | URL | Request) {
  const urlA = convertToURL(a);
  const urlB = convertToURL(b);

  return urlA.origin === urlB.origin;
}

function convertToURL(input: string | URL | Request): URL {
  if (input instanceof Request) {
    input = input.url;
  }

  try {
    return new URL(input);
  } catch {
    return new URL(input, window.origin);
  }
}
